import React, { useState } from "react";
import {
  Menu,
  Button,
  Drawer,
  Dropdown,
  Space,
  Grid,
  Badge,
} from "antd";
import {
  MenuOutlined,
  DownOutlined,
  PhoneOutlined,
  LogoutOutlined,
  ShoppingCartOutlined,
  StarFilled,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";

const { useBreakpoint } = Grid;

const onlineSellerLabel = (
  <span>
    <StarFilled style={{ color: "#722ed1", marginRight: 4 }} />
    Online Seller Products
  </span>
);

const Navbar = ({ userName, cartCount, allowToOrder, isOnlineSeller }) => {
  const navigate = useNavigate();
  const [drawerVisible, setDrawerVisible] = useState(false);
  const screens = useBreakpoint();

  // Track selected menu key
  const [selectedMenuKey, setSelectedMenuKey] = useState(() => {
    const currentPath = window.location.pathname;
    if (currentPath.includes("/online-seller-products")) return "onlineseller";
    if (currentPath.includes("/newarrival")) return "newarrival";
    if (currentPath.includes("/mydownloads")) return "mydownloads"; // added (for completeness)
    if (currentPath.includes("/aboutus")) return "aboutus";
    if (currentPath.includes("/contactus")) return "contactus";
    return "productcategories"; // default
  });

  // Helper to handle menu click and set selected key
  const handleMenuClick = (key, href) => {
    setSelectedMenuKey(key);
    if (href) {
      navigate(href);
    }
  };

  // Desktop menu items
  const desktopMenuItems = [
    {
      key: "productcategories",
      label: "Categories",
      href: "/productcategories",
    },
    // Only show New Arrival if user is logged in
    ...(userName
      ? [
          {
            key: "newarrival",
            label: "New Arrival",
            href: "/newarrival",
          },
        ]
      : []),
    // Only for customers marked Online Seller by admin
    ...(userName && isOnlineSeller
      ? [
          {
            key: "onlineseller",
            label: onlineSellerLabel,
            href: "/online-seller-products",
          },
        ]
      : []),
    { key: "aboutus", label: "About Us", href: "/aboutus" },
    { key: "contactus", label: "Contact", href: "/contactus" },
  ];

  const handleLogout = async () => {
    try {
      // Call the logout API
      const response = await fetch(
        global.config.APIURL.BaseURL + "api/Account/Logout",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      localStorage.removeItem("token");
      if (response.ok) {
        // Clear token from local storage
        localStorage.removeItem("token");

        // Redirect to login page
        navigate(0);
      } else {
        console.error("Failed to logout");
      }
    } catch (error) {
      console.error("Error during logout:", error);
    }
  };

  const resourcesMenu = (
    <Menu
      items={[
        {
          key: "1",
          label: <a href="/userdetailview">Account Detail</a>,
        },
        // Show My Orders only if allowToOrder is true
        ...(allowToOrder
          ? [
              {
                key: "2",
                label: <a href="/myorders">My Orders</a>,
              },
            ]
          : []),
        // My Estimates is not gated on allowToOrder: an estimate is a quote the seller pushed
        // to this customer, not something they raise themselves.
        {
          key: "myestimates",
          label: <a href="/myestimates">My Estimates</a>,
        },
        // Add My Downloads under the dropdown (not main menu)
        {
          key: "mydownloads",
          label: <a href="/mydownloads">My Downloads</a>,
        },
        {
          key: "3",
          label: <span onClick={() => handleLogout()}>Logout</span>,
          icon: <LogoutOutlined />,
        },
      ]}
    />
  );

  const mobileMenu = (
    <Menu mode="vertical">
      <Menu.Item key="Category">
        <a href="/productcategories">Categories</a>
      </Menu.Item>
      <Menu.Item key="About Us">
        <a href="/aboutus">About Us</a>
      </Menu.Item>
      <Menu.Item key="Contact">
        <a href="/contactus">Contact</a>
      </Menu.Item>
      {/* Show New Arrival only if logged in */}
      {userName && (
        <>
          <Menu.Item key="newarrival">
            <a href="/newarrival">New Arrival</a>
          </Menu.Item>
          {isOnlineSeller && (
            <Menu.Item key="onlineseller">
              <a href="/online-seller-products">{onlineSellerLabel}</a>
            </Menu.Item>
          )}
          <Menu.Item key="myestimates">
            <a href="/myestimates">My Estimates</a>
          </Menu.Item>
          {/* Add My Downloads in mobile menu for logged-in users */}
          <Menu.Item key="mydownloads">
            <a href="/mydownloads">My Downloads</a>
          </Menu.Item>
        </>
      )}
      <Menu.Item key="signin">
        {userName ? (
          <a href="/userdetailview"> {userName} </a>
        ) : (
          <a href="/login">Sign in</a>
        )}
      </Menu.Item>
      {/* My Orders for mobile, only if allowToOrder */}
      {userName && allowToOrder && (
        <Menu.Item key="myorders">
          <a href="/myorders">My Orders</a>
        </Menu.Item>
      )}
      {/* Cart only if allowToOrder */}
      {allowToOrder && (
        <Menu.Item key="cart">
          <Badge count={cartCount} size="small" offset={[8, 0]}>
            <Button
              type="link"
              icon={<ShoppingCartOutlined />}
              onClick={() => navigate("/cart")}
              style={{ padding: 0 }}
            >
              Cart
            </Button>
          </Badge>
        </Menu.Item>
      )}
      <Menu.Item key="callback">
        <Button
          type="primary"
          block
          icon={<PhoneOutlined />}
          onClick={() => navigate("/callbackpage")}
        >
          Call Back
        </Button>
      </Menu.Item>
      {userName ? (
        <Menu.Item key="logout">
          <Button
            type="link"
            block
            icon={<LogoutOutlined />}
            onClick={() => handleLogout()}
          >
            Logout
          </Button>
        </Menu.Item>
      ) : (
        <div></div>
      )}
    </Menu>
  );

  // Add useEffect to update selected menu when location changes
  React.useEffect(() => {
    const currentPath = window.location.pathname;
    if (currentPath.includes("/online-seller-products")) {
      setSelectedMenuKey("onlineseller");
    } else if (currentPath.includes("/newarrival")) {
      setSelectedMenuKey("newarrival");
    } else if (currentPath.includes("/mydownloads")) {
      setSelectedMenuKey("mydownloads"); // added (for completeness)
    } else if (currentPath.includes("/aboutus")) {
      setSelectedMenuKey("aboutus");
    } else if (currentPath.includes("/contactus")) {
      setSelectedMenuKey("contactus");
    } else if (
      currentPath.includes("/productcategories") ||
      currentPath === "/"
    ) {
      setSelectedMenuKey("productcategories");
    }
  }, []);

  return (
    <div
      style={{
        background: "#fff",
        padding: "10px 24px",
        borderRadius: "999px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        maxWidth: 1200,
        margin: "20px auto",
      }}
    >
      {/* Logo */}
      <div>
        <img
          src="https://thejoj.com/images/jojlogo.png"
          alt="Logo"
          style={{ height: 28 }}
        />
      </div>

      {/* Desktop Menu */}
      {screens.md ? (
        <>
          <Space size="large">
            {desktopMenuItems.map((item) => (
              <a
                key={item.key}
                href={item.href}
                onClick={(e) => {
                  e.preventDefault();
                  handleMenuClick(item.key, item.href);
                }}
                style={{
                  color: selectedMenuKey === item.key ? "#1677ff" : undefined,
                  fontWeight: selectedMenuKey === item.key ? 600 : undefined,
                  borderBottom:
                    selectedMenuKey === item.key ? "2px solid #1677ff" : "none",
                  paddingBottom: 2,
                  transition: "all 0.2s",
                }}
              >
                {item.label}
              </a>
            ))}
          </Space>
          <Space size="middle">
            {/* Cart only if allowToOrder */}
            {allowToOrder && (
              <Badge count={cartCount} size="small" offset={[8, 0]}>
                <a
                  href="/cart"
                  style={{ display: "inline-flex", alignItems: "center" }}
                >
                  <ShoppingCartOutlined style={{ fontSize: 18 }} />
                </a>
              </Badge>
            )}
            {userName ? (
              <Dropdown overlay={resourcesMenu}>
                <span style={{ cursor: "pointer" }}>
                  {userName} <DownOutlined />
                </span>
              </Dropdown>
            ) : (
              <a href="/login">Sign in</a>
            )}
            <Button
              type="primary"
              block
              icon={<PhoneOutlined />}
              onClick={() => navigate("/callbackpage")}
            >
              Call Back
            </Button>
          </Space>
        </>
      ) : (
        // Mobile: Cart icon before menu button, only if allowToOrder
        <div style={{ display: "flex", alignItems: "center" }}>
          {allowToOrder && (
            <Badge count={cartCount} size="small" offset={[0, 0]}>
              <Button
                type="text"
                icon={<ShoppingCartOutlined style={{ fontSize: 22 }} />}
                onClick={() => navigate("/cart")}
                style={{ marginRight: 8, padding: 0 }}
              />
            </Badge>
          )}
          <Button
            type="text"
            icon={<MenuOutlined />}
            onClick={() => setDrawerVisible(true)}
          />
        </div>
      )}

      {/* Drawer for mobile */}
      <Drawer
        placement="right"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        title="Menu"
      >
        {mobileMenu}
      </Drawer>
    </div>
  );
};

export default Navbar;
